import React, { ReactNode, useContext, useMemo } from 'react';
import { useQuery } from 'react-query';
import { DefaultTheme, ThemeProvider } from 'styled-components';
import { createTheme, ThemeProvider as MUITheProvider } from '@mui/material';
import usePersistedState from '../hooks/usePersistedState';
import { TenantController } from '../controller/TenantController';
import { IFieldsApplicationViewModel, IUiVisibilitySettingsViewModel } from 'viewModels';

type ConfigCustomContextProps = {
  children: ReactNode;
};
interface ICorrespondenceType {
  [key: string]: string;
}

const ConfigCustomContext = React.createContext<{
  theme: DefaultTheme | undefined;
  setTheme: React.Dispatch<React.SetStateAction<DefaultTheme | undefined>>;
  enumCorrespondenceType: ICorrespondenceType | undefined;
  pathResourceEmail: string | undefined;
  fieldsRequireds: IFieldsApplicationViewModel | undefined;
  setFieldsRequireds: React.Dispatch<React.SetStateAction<IFieldsApplicationViewModel | undefined>>;
  uiVisibilitySettings: IUiVisibilitySettingsViewModel | undefined;
}>({
  theme: undefined,
  setTheme: () => {},
  pathResourceEmail: undefined,
  enumCorrespondenceType: undefined,
  fieldsRequireds: undefined,
  setFieldsRequireds: () => {},
  uiVisibilitySettings: undefined,
});

const ConfigCustomProvider = ({ children }: ConfigCustomContextProps) => {
  const [theme, setTheme] = usePersistedState<DefaultTheme | undefined>(
    'themeApp',
    undefined,
    true,
  );
  const [enumCorrespondenceType, setEnumCorrespondenceType] = usePersistedState<
    ICorrespondenceType | undefined
  >('enumCorrespondenceType', undefined, true);
  const [fieldsRequireds, setFieldsRequireds] = usePersistedState<
    IFieldsApplicationViewModel | undefined
  >('fieldsRequireds', undefined, true);
  const [pathResourceEmail, setPathResourceEmail] = usePersistedState<string | undefined>(
    'pathResourceEmail',
    undefined,
    true,
  );
  const [uiVisibilitySettings, setUiVisibilitySettings] = usePersistedState<
    IUiVisibilitySettingsViewModel | undefined
  >('uiVisibilitySettings', undefined, true);

  useQuery('configs', () => TenantController.getConfigs(), {
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      setTheme({
        title: 'light',
        colors: {
          ...data.colors,
        },
      });
      setEnumCorrespondenceType(data.enums.correspondence_type);
      setPathResourceEmail(data.paths.template_email);
      setFieldsRequireds(data.fieldsRequireds);
      setUiVisibilitySettings(data.uiVisibilitySettings);
    },
    enabled:
      !theme ||
      !enumCorrespondenceType ||
      !pathResourceEmail ||
      !fieldsRequireds ||
      !uiVisibilitySettings,
  });

  const contextValues = useMemo(
    () => ({
      theme,
      pathResourceEmail,
      fieldsRequireds,
      enumCorrespondenceType,
      setFieldsRequireds,
      setTheme,
      uiVisibilitySettings,
    }),
    [
      theme,
      pathResourceEmail,
      fieldsRequireds,
      enumCorrespondenceType,
      setFieldsRequireds,
      setTheme,
      uiVisibilitySettings,
    ],
  );

  if (!theme) return null;

  const MUItheme = createTheme({
    palette: {
      primary: {
        main: theme.colors.primary,
      },
      secondary: {
        main: theme.colors.secondary,
      },
      info: {
        main: theme.colors.tertiary_800,
      },
    },
  });

  return (
    <ConfigCustomContext.Provider value={contextValues}>
      <MUITheProvider theme={MUItheme}>
        <ThemeProvider theme={theme}>{children}</ThemeProvider>
      </MUITheProvider>
    </ConfigCustomContext.Provider>
  );
};

export const useConfig = () => useContext(ConfigCustomContext);
export default ConfigCustomProvider;
